<template>
  <u-deploy-panel :selected="!!selected" :sideinfo="true">
    <template slot="title">
      {{ $t('title') }}
    </template>
    <template slot="content">
      <p>{{ $t('headline') }}</p>
      <v-container class="pa-0" fluid grid-list-xs text-center>
        <u-deploy-selection
          :items="locations"
          :selected="selected"
          @toggle="toggle"
        ></u-deploy-selection>
        <u-deploy-selection
          :items="serviceLocations"
          :selected="selected"
          @toggle="toggle"
        ></u-deploy-selection>
      </v-container>
    </template>
    <template slot="actions">
      <a :href="docs" target="_blank">{{ $t('wizard.more') }}</a>
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('cancel')">{{ $t('wizard.cancel') }}</v-btn>
      <v-btn text @click="$emit('prev')">{{ $t('wizard.prev') }}</v-btn>
      <v-btn color="primary" type="submit" value="Submit" :disabled="!selected" @click="$emit('next', selected)" >{{ $t('wizard.next') }}</v-btn>
    </template>
    <template slot="empty">
      {{ $t('select') }}
    </template>
    <template slot="selection" v-if="selected">
      {{ $t('items.' + selected.id + '.title') }}
    </template>
    <template slot="info" v-if="selected">
      <v-tab ripple>{{ $t('tabs.info.title') }}</v-tab>
      <v-tab-item>
        <div class="mt-3" v-if="selected.info_path">
          <i18n v-if="selected" :path="selected.info_path">
            <span class="accent--text" place="device">{{ device.name }}</span>
          </i18n>
        </div>
        <template v-if="selected.paragraphs">
          <div v-for="paragraph in selected.paragraphs" :key="paragraph">
            <u-deploy-info
              class="mt-3"
              :info="paragraph"
            ></u-deploy-info>
          </div>
        </template>
        <u-deploy-info
          class="mt-3"
          v-if="selected.title_offers"
          :info="selected.title_offers"
        ></u-deploy-info>
        <ul>
          <template v-if="selected.offers">
            <li v-for="offer in selected.offers" :key="offer">
              {{ offer }}
            </li>
          </template>
        </ul>
        <u-deploy-info
          class="mt-3"
          v-if="selected.conclusion"
          :info="selected.conclusion"
        ></u-deploy-info>
        <div v-if="selected.faq" class="mt-3">
          <i18n path="tabs.info.faq">
            <a place="faq" href="https://www.unryo.com">{{ $t('installation') }}</a>
          </i18n>
        </div>
      </v-tab-item>
      <v-tab ripple disabled>{{ $t('tabs.pricing.title') }}</v-tab>
      <v-tab-item>
        <u-deploy-info
          class="mt-3"
          v-if="selected.pricing"
          :info="selected.pricing"
        ></u-deploy-info>
      </v-tab-item>
    </template>
  </u-deploy-panel>
</template>

<script>
import DeployInfoComponent from '@/components/deploy/Info.vue'
import DeployPanelComponent from '@/components/deploy/Panel.vue'
import DeploySelectionComponent from '@/components/deploy/Selection.vue'
import { docsUrl } from '@/services/docs'

export default {
  data () {
    return {
      selected: null,
      locations: [
        {
          id: 'private',
          name: this.$t('items.private.name'),
          info_path: 'items.private.description',
          image: `${require('../assets/private.svg')}`,
          pricing: this.$t('tabs.pricing.description'),
          available: true
        },
        {
          id: 'na',
          name: this.$t('items.na.name'),
          image: `${require('../assets/na.svg')}`,
          info_path: 'items.na.description',
          pricing: this.$t('tabs.pricing.description'),
          available: false
        },
        {
          id: 'eu',
          name: this.$t('items.eu.name'),
          region: this.$t('location.region.eu'),
          info_path: 'items.eu.description',
          image: `${require('../assets/eu.svg')}`,
          pricing: this.$t('tabs.pricing.description'),
          available: false
        }
      ],
      serviceLocations: [
        {
          id: 'amazon',
          name: this.$t('items.amazon.name'),
          image: `${require('../assets/amazon.svg')}`,
          info_path: 'items.amazon.description',
          title_offers: this.$t('items.amazon.title_offers'),
          offers: this.$t('items.amazon.offers'),
          conclusion: this.$t('items.amazon.conclusion'),
          pricing: this.$t('tabs.pricing.description'),
          available: false
        },
        {
          id: 'azure',
          name: this.$t('items.azure.name'),
          image: `${require('../assets/azure.svg')}`,
          info_path: 'items.azure.description',
          paragraphs: this.$t('items.azure.paragraphs'),
          title_offers: this.$t('items.azure.title_offers'),
          offers: this.$t('items.azure.offers'),
          conclusion: this.$t('items.azure.conclusion'),
          pricing: this.$t('tabs.pricing.description'),
          available: false
        },
        {
          id: 'google',
          name: this.$t('items.google.name'),
          image: `${require('../assets/google.svg')}`,
          info_path: 'items.google.description',
          pricing: this.$t('tabs.pricing.description'),
          available: false
        }
      ],
      errors: [],
      tabs: {
        info: { title: this.$t('tabs.info.title') },
        pricing: { title: this.$t('tabs.pricing.title') }
      },
      docs: docsUrl('quickstart', 'deployment-methods')
    }
  },
  props: {
    device: Object
  },
  methods: {
    toggle (item) {
      this.selected = this.selected === item ? this.unselected : item
    }
  },
  components: {
    'u-deploy-info': DeployInfoComponent,
    'u-deploy-panel': DeployPanelComponent,
    'u-deploy-selection': DeploySelectionComponent
  }
}
</script>

<style lang="scss" scoped>
.description {
  white-space: pre-wrap;
}
</style>

<i18n>
{
  "en": {
    "title": "Where do you want to deploy this component?",
    "headline": "Components are platform-agnostic and can be deployed on-prem or in a public cloud. You can also use them as-a-service by selecting an Unryo cloud region. No worry, the location can be changed later as Unryo allows on-the-fly moves at any time.",
    "second_headline": "No worry, the location can be changed later: Unryo allows on-the-fly moves of components at any time, from one location to another.",
    "installation": "installation",
    "select": "Please select a location",
    "items": {
      "private": {
        "name": "Your Private Infrastructure",
        "title": "Your Private Infrastructure",
        "text": "your private Infrastructure",
        "description": "Your current selection is set to deploy a {device} on your private infrastructure."
      },
      "na": {
        "name": "Unryo Cloud North America",
        "title": "Unryo Cloud North America",
        "text": "UNRYO Cloud",
        "description": "The Unryo production infrastructure is hosted in Microsoft Azure."
      },
      "eu": {
        "name": "Unryo Cloud Europe",
        "title": "Unryo Cloud Europe",
        "text": "UNRYO Cloud",
        "description": "The Unryo production infrastructure is hosted in Microsoft Azure."
      },
      "amazon": {
        "name": "Amazon Web Services",
        "title": "Amazon Web Services",
        "text": "Amazon Web Services",
        "description": "You can use AWS as a cloud computing option to run your ( {device} ) on Amazon Web Services",
        "paragraphs": {
          "p1": "Being hybrid multi-clouds, we support all your deployment preferences. All the Unryo components are platform-agnostic, so you can run them on your preferred cloud infrastructure provider.",
          "p2": "The Unryo Registry Server will guarantee component interconnectivity even if your components run on different locations – cloud vendors or on-premises – so we support your hybrid multi-cloud strategy."
        },
        "conclusion": "We are closely engaged with AWS teams to deliver superior service experience.",
        "title_offers": "We offer Unryo in the following AWS regions:",
        "offers": {
          "east": "East-US-1 region",
          "west": "West-US-1 region",
          "amazon": "More Amazon regions coming in the months ahead"
        }
      },
      "azure": {
        "name": "Microsoft Azure Services",
        "title": "Microsoft Azure",
        "text": "Azure Web Services",
        "description": "You can use Microsoft Azure as a cloud computing option to run your ( {device} ).",
        "paragraphs": {
          "p1": "Being hybrid multi-clouds, we support all your deployment preferences. All the Unryo components are platform-agnostic, so you can run them on your preferred cloud infrastructure provider.",
          "p2": "The Unryo Registry Server will guarantee component interconnectivity even if your components run on different locations – cloud vendors or on-premises – so we support your hybrid multi-cloud strategy."
        },
        "conclusion": "We are closely engaged with Microsoft and Azure teams to deliver superior service experience.",
        "title_offers": "We offer Unryo in the following Azure regions:",
        "offers": {
          "east": "East-US-1 region",
          "west": "West-US-1 region",
          "azure": "More Azure regions coming in the months ahead"
        }
      },
      "google": {
        "name": "Google Cloud Platform",
        "title": "Google Cloud Services",
        "text": "Google Cloud Services",
        "description": "Your current selection is set to deploy a ( {device} ) on Google Cloud Web Services."
      }
    },
    "tabs": {
      "info": {
        "title": "Info",
        "faq": "See {faq} FAQs"
      },
      "pricing": {
        "title": "Pricing",
        "description": "Pricing is based on the total number of devices that will have their metrics stored in this database. The more devices being monitoring, the more metrics will be stored and bigger will be the database (should we force a static retention period or propose options?)"
      }
    }
  }
}
</i18n>
